import { useUnit } from 'effector-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updatePositionFx, type Position } from '../../entities/position';
import { parseDecimal } from '../../shared/lib/decimal';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { Button, Dialog, DialogContent, Field, Input } from '../../shared/ui';
import { $error, $position, closed } from './model';

export function EditTargetDialog() {
  const [position, close] = useUnit([$position, closed]);
  return (
    <Dialog
      open={position !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      {position && <Form key={position.id} position={position} />}
    </Dialog>
  );
}

function Form({ position }: { position: Position }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [value, setValue] = useState(
    locale === 'bg' ? position.target_pct.replace('.', ',') : position.target_pct,
  );
  const [error, pending, close, save] = useUnit([
    $error,
    updatePositionFx.pending,
    closed,
    updatePositionFx,
  ]);
  const parsed = parseDecimal(value, locale);
  return (
    <DialogContent
      title={t('editTarget.title')}
      description={t('editTarget.description')}
      closeLabel={t('common.close')}
      onSubmit={() => {
        if (parsed !== null)
          void save({ id: position.id, patch: { target_pct: parsed } }).catch(() => undefined);
      }}
      footer={
        <>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={pending || parsed === null}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <Field
        label={t('openPosition.target')}
        error={error ?? (value && parsed === null ? t('form.notANumber') : undefined)}
      >
        {({ id, describedBy, invalid }) => (
          <Input
            id={id}
            inputMode="decimal"
            numeric
            unit="%"
            value={value}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        )}
      </Field>
    </DialogContent>
  );
}
